import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import Home from './components/Home';
import { PrivateRoute, PublicRoute } from './routes/ProtectedRoutes';
import SeeContent from './components/SeeContent';
import Camera from './components/Camera';
import Flashes from './components/Flashes';
import ProfileUpload from './components/ProfileUpload';
import Profile from './components/Profile';
import TrippinPostView from './components/TrippinPostView';
import YourTrippins from './components/YourTrippins';
import TrippinPlaceSelect from './components/TrippinPlaceSelect';
import TrippinPhotoSelect from './components/TrippinPhotoSelect';
import TrippinReviewPost from './components/TrippinReviewPost';
import ErrorBoundary from './components/ErrorBoundary';
import { connectSocket } from './utils/WebSocket';
import { jwtDecode } from 'jwt-decode';
import Notifications from './components/Notifications';
import PlaceView from "./components/PlaceView";
import Saved from "./components/Saved";
import UserFlashes from './components/UserFlashes';
import Flash from './components/Flash';
import StalkersList from './components/StalkersList';
import StalkingList from './components/StalkingList';
import EditCloseFriends from './components/EditCloseFirends';
import Search from './components/Search';
import { App as CapacitorApp } from '@capacitor/app';
import MyTrippinGrid from './components/MyTrippinGrid';
import TrippinsTabs from './components/TrippinTabs';
import MyTrippinTabs from './components/MyTrippinTabs';
import { PushNotifications } from '@capacitor/push-notifications';
import { isPlatform } from '@ionic/react';
import ChangePassword from './components/ChangePassword';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');

    if (accessToken) {
      try {
        const decodedToken = jwtDecode(accessToken);
        const userId = decodedToken.userId;

        if (userId) {
          connectSocket(userId);
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  });

  useEffect(() => {
    // ONLY run on mobile platforms
    if (isPlatform('android')) {
      const accessToken = localStorage.getItem('accessToken');

    if (accessToken) {
      try {
      // Function to register for push notifications and save the token
      const registerPushNotifications = async () => {
        try {
          // Check the permission status
          const permissionStatus = await PushNotifications.checkPermissions();
          if (permissionStatus.receive !== 'granted') {
            permissionStatus = await PushNotifications.requestPermissions();
            console.log("Requested permission status:", permissionStatus);
          }
          if (permissionStatus.receive === 'granted') {
            console.log('Permission granted. Registering for push notifications...');  
            // Handle successful registration
            PushNotifications.addListener('registration', async (token) => {
              console.log('Push registration success:', token.value);
              const accessToken = localStorage.getItem('accessToken');
  
              // Save the FCM token to the backend
              await fetch('https://trippin.icu/api/device-tokens', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ deviceToken: token.value }),
              })
                .then(response => response.json())
                .then(data => console.log("Token stored successfully:", data))
                .catch(error => console.error("Error saving token:", error));
            });
  
            // Handle registration error
            PushNotifications.addListener('registrationError', (error) => {
              console.error('Push registration error:', error);
            });
            await PushNotifications.register();
          } else {
            console.warn('Push notifications permission not granted.');
          }
        } catch (error) {
          console.error('Error during push notification registration:', error);
        }
      };
  
      // Register for push notifications when the app is in the foreground
      CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          console.log('App is active. Checking push notification permissions...');
          await registerPushNotifications();
        }
        
      });
  
      registerPushNotifications();
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }
    }
    
  }, []);

  useEffect(() => {
    if (isPlatform('ios')) {
      const accessToken = localStorage.getItem('accessToken');

      if (accessToken) {
        console.log("🔄 Waiting for FCM token from iOS...");

        // Listen for event from Swift (token will be sent from AppDelegate)
        window.addEventListener("FCMTokenReceived", async (event) => {
          const token = event.detail.token;
          console.log("✅ Received FCM Token from Native:", token);

          try {
            await fetch('https://trippin.icu/api/device-tokens', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({ deviceToken: token }),
            });
            console.log("✅ Token stored successfully in backend");
          } catch (error) {
            console.error("❌ Error saving token:", error);
          }
        });

        console.log("📡 Waiting for FCM token event...");
      }
    }
  }, []);

  useEffect(() => {
    let backButtonListener;

    const handleBackButton = () => {
      if (location.pathname === '/home' || location.pathname === '/login') {
        // Exit app if on the home or login page
        CapacitorApp.exitApp();
      } else {
        // Navigate back
        navigate(-1);
      }
    };

    // Add listener for hardware back button
    const addBackButtonListener = async () => {
      backButtonListener = await CapacitorApp.addListener('backButton', handleBackButton);
    };

    addBackButtonListener();

    // Cleanup listener on component unmount
    return () => {
      if (backButtonListener && typeof backButtonListener.remove === 'function') {
        backButtonListener.remove(); // Properly remove the listener
      }
    };
  }, [location, navigate]);
  
  if ('serviceWorker' in navigator) {
    if (!window.Capacitor || !window.Capacitor.isNativePlatform()) {
      navigator.serviceWorker
        .register('/firebase-messaging-sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    } else {
      console.log('Skipping Service Worker for Capacitor WebView');
    }
  }
  
  return ( 
    <ErrorBoundary>  
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <Signup />
            </PublicRoute>
          }
        />

        {/* Private Routes */}
        <Route
          path="/home"
          element={
            <PrivateRoute>
              <Home />
            </PrivateRoute>
          }
        />
        <Route
          path="/search"
          element={
            <PrivateRoute>
              <Search />
            </PrivateRoute>
          }
        />
        <Route
          path="/flashes"
          element={
            <PrivateRoute>
              <Flashes />
            </PrivateRoute>
          }
        />
        <Route
          path="/camera"
          element={
            <PrivateRoute>
              <Camera />
            </PrivateRoute>
          }
        />
        <Route
          path="/see-content"
          element={
            <PrivateRoute>
              <SeeContent />
            </PrivateRoute>
          }
        />

        {/* Default */}
        <Route path="*" element={<Navigate to="/home" replace />} />
        <Route path="/profile-upload" element={<PrivateRoute><ProfileUpload /></PrivateRoute>} />
        <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
        <Route path="/profile/:userIdPar" element={<PrivateRoute><Profile /></PrivateRoute>} />
        <Route path="/trippins/:postId" element={<PrivateRoute><TrippinPostView /></PrivateRoute>} />
        <Route path="/mytrippins/:trippinId" element={<PrivateRoute><TrippinPostView /></PrivateRoute>} />
        <Route path="/mytrippins/tagged/:trippinId" element={<PrivateRoute><TrippinPostView /></PrivateRoute>} />
        <Route path="/yourtrippins/:trippinId/placesSelect" element={<PrivateRoute><TrippinPlaceSelect isTagged={false}/></PrivateRoute>} />
        <Route path="/taggedtrippins/:trippinId/placesSelect" element={<PrivateRoute><TrippinPlaceSelect isTagged={true} /></PrivateRoute>} />
        <Route path="/taggedtrippins/:trippinId/photoSelect/:placeName" element={<PrivateRoute><TrippinPhotoSelect isTagged={true}/></PrivateRoute>} />          <Route path="/yourtrippins/:trippinId/photoSelect/:placeName" element={<PrivateRoute><TrippinPhotoSelect isTagged={false}/></PrivateRoute>} />
        <Route path="/taggedtrippins/:trippinId/reviewPost" element={<PrivateRoute><TrippinReviewPost isTagged={true}/></PrivateRoute>} />
        <Route path="/yourtrippins/:trippinId/reviewPost" element={<PrivateRoute><TrippinReviewPost isTagged={false}/></PrivateRoute>} />
        <Route path="/yourtrippins" element={<PrivateRoute><YourTrippins /></PrivateRoute>} />
        <Route path="/notifications" element={<PrivateRoute><Notifications /></PrivateRoute>} />
        <Route path="/trippinpostgrid" element={<PrivateRoute><Profile /></PrivateRoute>} />
        <Route path="/saved" element={<PrivateRoute><Saved /></PrivateRoute>} />
        <Route path="/places/:placeId" element={<PrivateRoute><PlaceView /></PrivateRoute>} />
        <Route path="/profile/:userId/stalkers" element={<PrivateRoute><StalkersList /></PrivateRoute>} />
        <Route path="/profile/:userId/stalking" element={<PrivateRoute><StalkingList /></PrivateRoute>} />
        <Route path="/user-flashes/:userId" element={<UserFlashes />} />
        <Route path="/flash/:flashId" element={<Flash />} />
        <Route path="/edit-close-friends" element={<PrivateRoute><EditCloseFriends /></PrivateRoute>} />
        <Route path="/mytrippins" element={<PrivateRoute><MyTrippinGrid /></PrivateRoute>} />
        <Route path="/tabs" element={<PrivateRoute><TrippinsTabs /></PrivateRoute>} />
        <Route path="/mytabs" element={<PrivateRoute><MyTrippinTabs  /></PrivateRoute>} />
        <Route path="/yourtrippins/:trippinId/photoSelect/:placeName" element={<PrivateRoute><TrippinPhotoSelect isTagged={false}/></PrivateRoute>} />
        <Route path="/trippin/:userId/country/:isoCode" element={<PrivateRoute><TrippinPostView /></PrivateRoute>} />
        <Route
          path="/change-password"
          element={
            <PrivateRoute>
              <ChangePassword />
            </PrivateRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password/:token"
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />
      </Routes> 
    </ErrorBoundary>
  );
}

export default App;